// CB:BEGIN homeowner-documents-page
/**
 * Documents page for homeowner: signed contract, invoice, accepted quote per project.
 */
import React, { useEffect, useState } from 'react';
import { useApi } from '../lib/useApi';
import { useAuthFetch } from '../lib/useAuthFetch';
import { getApiUrl } from '../lib/getApiUrl';
import ResponsiveContainer from '../components/layout/ResponsiveContainer';
import SharedHeaderCustomer from '../components/SharedHeaderCustomer';

interface ProjectDocuments {
  projectId: string;
  address: string;
  documents: {
    signedContractUrl: string | null;
    signedContractLabel: string;
    invoiceUrl: string | null;
    invoiceLabel: string;
    acceptedQuoteUrl: string | null;
    acceptedQuoteLabel: string;
    proposalBidId: string | null;
  };
}

const DocButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    className="btn btn-primary"
    style={{ fontSize: 14, width: 'auto', whiteSpace: 'nowrap' }}
    onClick={onClick}
  >
    {label}
  </button>
);

const HomeownerDocumentsPage: React.FC<{ onNavigate: (screen: string) => void }> = ({ onNavigate }) => {
  const { get } = useApi();
  const authFetch = useAuthFetch();
  const [projects, setProjects] = useState<ProjectDocuments[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get<{ projects: ProjectDocuments[] }>('/api/homeowner/documents');
        if (!cancelled && res?.projects) setProjects(res.projects);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load documents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [get]);

  const openSignedUrl = (url: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openProposalPdf = async (projectId: string, bidId: string) => {
    const key = `${projectId}:${bidId}`;
    if (openingId === key) return;
    setOpeningId(key);
    try {
      const url = getApiUrl(`/api/projects/${projectId}/bids/${bidId}/quote-pdf`);
      const res = await authFetch(url);
      if (!res.ok) return;
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (e) {
      console.error('Failed to open proposal:', e);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <ResponsiveContainer className="container">
      <SharedHeaderCustomer onNavigate={onNavigate} />
      <div className="content">
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Documents</h1>
          <p style={{ color: '#6b7280', fontSize: 16 }}>
            View and download your signed contract, invoice, and accepted quote by project.
          </p>
        </div>

        {loading && (
          <p style={{ color: '#6b7280' }}>Loading documents…</p>
        )}
        {error && (
          <p style={{ color: '#dc2626' }}>{error}</p>
        )}
        {!loading && !error && projects.length === 0 && (
          <div className="dashboard-card" style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ color: '#6b7280', margin: 0 }}>
              You don't have any projects with accepted bids yet. Once you accept a bid, your invoice and contract will appear here.
            </p>
          </div>
        )}
        {!loading && !error && projects.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {projects.map((p) => (
              <div key={p.projectId} className="dashboard-card" style={{ padding: 24 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>{p.address || `Project ${p.projectId}`}</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                  {p.documents.signedContractUrl && (
                    <DocButton
                      label={`View ${p.documents.signedContractLabel}`}
                      onClick={() => openSignedUrl(p.documents.signedContractUrl)}
                    />
                  )}
                  {p.documents.invoiceUrl && (
                    <DocButton
                      label={`View ${p.documents.invoiceLabel}`}
                      onClick={() => openSignedUrl(p.documents.invoiceUrl)}
                    />
                  )}
                  {p.documents.proposalBidId && (
                    <DocButton
                      label={openingId === `${p.projectId}:${p.documents.proposalBidId}` ? 'Opening…' : 'View Proposal'}
                      onClick={() => openProposalPdf(p.projectId, p.documents.proposalBidId!)}
                    />
                  )}
                  {!p.documents.signedContractUrl && !p.documents.invoiceUrl && !p.documents.proposalBidId && (
                    <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>
                      Documents will appear here after you sign the contract and the invoice is generated.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ResponsiveContainer>
  );
};

export default HomeownerDocumentsPage;
// CB:END homeowner-documents-page
